import { TOOLS, type Tool } from '../fab/layout'
import type { HmiReadout } from '../fab/hmi'
import type { PanelServices, ToolPanelDef } from './types'
import { storedPanelState } from '../learn/SessionStore'

/**
 * Tool id to tool panel, one to one.
 *
 * This function is the entire seam between the floor and what runs on it. A
 * tool carries no behaviour and a panel carries no position; the id in
 * `TOOLS` is what binds them, and nothing else crosses.
 *
 * A panel is a session-long STORE plus the React components that project it
 * (see ToolPanelDef in types.ts). The store is built here, once, at open -
 * outside React, because that is where a solver-shaped constructor belongs.
 *
 * WHY THE PANELS ARE IMPORTED, NOT REFERENCED. Every panel drags its physics
 * in behind it - solvers, chart canvases, an assistant and its intent table.
 * Named at the top of this file, all twenty-five land in the entry chunk and a
 * student downloads the whole fab to stand in the gowning room. Behind
 * `import()` each one is its own chunk, fetched the first time somebody opens
 * that tool. That is the only reason the lookup is asynchronous.
 *
 * WHY THE FETCH AND THE BUILD ARE SEPARATE STEPS. The two costs are nothing
 * alike. The fetch is network, off the main thread, and free to do early; the
 * build runs a constructor that stands up a solver and often SOLVES it -
 * DepositionSim runs a full transport recompute before its panel exists - and
 * that is main-thread work that freezes the frame it lands on. The first
 * version fused them, so merely looking at a tool from the aisle (the preload)
 * built its whole panel, and the walk hitched once per tool as the student's
 * gaze crossed it. Now `preloadToolPanel` warms the chunk only, and the
 * constructor runs at open, where the camera glide is already covering the
 * beat. (This is also why React.lazy is never used here - it fuses the two
 * steps back together.)
 */

interface PanelEntry {
  /** Warm the code. Nothing is constructed; a second call is a cache hit. */
  chunk: () => Promise<unknown>
  /** Fetch the (usually already-warm) code; still constructs nothing. */
  resolve: () => Promise<ToolPanelDef<unknown>>
}

/** Tie a chunk to the def it exports. Neither step constructs a store. */
const entry = <M,>(
  chunk: () => Promise<M>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each def
  // carries its own store type; the registry erases it to unknown.
  pick: (m: M) => ToolPanelDef<any>,
): PanelEntry => ({
  chunk,
  resolve: () => chunk().then((m) => pick(m) as ToolPanelDef<unknown>),
})

/**
 * The lookup. One entry per live tool id, and the id must match `TOOLS`.
 *
 * A missing id is a bug rather than a fallback: it used to land on the etcher,
 * so a typo in a tool id opened a plasma etcher under some other tool's name
 * and nothing said so.
 */
const PANELS: Record<string, PanelEntry> = {
  gowning: entry(() => import('./SupportPanelDefs'), (m) => m.cleanroomPanel),
  crystal: entry(() => import('./SubstratePanelDefs'), (m) => m.crystalPanel),
  waferprep: entry(() => import('./SubstratePanelDefs'), (m) => m.waferPrepPanel),
  epi: entry(() => import('./EpiPanelDef'), (m) => m.epiPanel),
  clean: entry(() => import('./CleanPanel'), (m) => m.cleanPanel),
  implant: entry(() => import('./ImplantPanel'), (m) => m.implantPanel),
  etch: entry(() => import('./EtchPanelDef'), (m) => m.etchPanel),
  deposition: entry(() => import('./DepositionPanelDef'), (m) => m.depositionPanel),
  litho: entry(() => import('./LithoPanelDef'), (m) => m.lithoPanel),
  patterning: entry(() => import('./SaqpPanelDef'), (m) => m.saqpPanel),
  thermal: entry(() => import('./ThermalPanelDef'), (m) => m.thermalPanel),
  metrology: entry(() => import('./MetrologyPanelDef'), (m) => m.metrologyPanel),
  facilities: entry(() => import('./FacilitiesPanelDef'), (m) => m.facilitiesPanel),
  cmp: entry(() => import('./CmpPanelDef'), (m) => m.cmpPanel),
  plating: entry(() => import('./SupportPanelDefs'), (m) => m.platingPanel),
  inspection: entry(() => import('./SortPanelDefs'), (m) => m.inspectionPanel),
  probe: entry(() => import('./SortPanelDefs'), (m) => m.probePanel),
  dicing: entry(() => import('./AssemblyPanelDefs'), (m) => m.dicingPanel),
  bonding: entry(() => import('./AssemblyPanelDefs'), (m) => m.bondingPanel),
  packaging: entry(() => import('./AssemblyPanelDefs'), (m) => m.packagingPanel),
  finaltest: entry(() => import('./AssemblyPanelDefs'), (m) => m.finalTestPanel),
  gas: entry(() => import('./SupportPanelDefs'), (m) => m.gasPanel),
  euv: entry(() => import('./EuvPanelDef'), (m) => m.euvPanel),
  ebeam: entry(() => import('./SemPanelDef'), (m) => m.semPanel),
  quantum: entry(() => import('./QuantumPanelDef'), (m) => m.quantumPanel),
}

/**
 * Which entry answers for this id - the live panel, or the scope page for a
 * tool whose simulation is not written yet. Null for a live tool with no
 * panel, which `loadToolPanel` turns into the loud error it deserves.
 */
function entryFor(id: string): PanelEntry | null {
  const tool = TOOLS.find((s) => s.id === id)
  if (tool?.status !== 'live') {
    // The scope page: what arrives, what leaves, the protocol, the physics
    // it will be built on. A work plan for a tool standing before its
    // simulation is written, not a placeholder.
    return entry(() => import('./ScopePanelDef'), (m) => m.scopePanelFor(id))
  }
  return PANELS[id] ?? null
}

/** A built panel - its def and the store that def created. Kept for the session. */
export interface BuiltPanel {
  def: ToolPanelDef<unknown>
  store: unknown
}

const cache = new Map<string, BuiltPanel>()
/**
 * Panels whose chunk is in flight.
 *
 * Without this, opening a tool twice before its chunk lands - a click, an
 * escape, a click again, or a language switch mid-fetch - builds the panel
 * twice and throws the first one away along with whatever the student had
 * already typed into it.
 */
const pending = new Map<string, Promise<BuiltPanel>>()

/**
 * What this tool's own screen shows, if its panel exists to answer - null
 * sends the caller to idleReadout. The aisle paints screens several times a
 * second, so this must never construct anything.
 */
export function peekPanelReadout(id: string): HmiReadout | null {
  const built = cache.get(id)
  return built?.def.hmi?.(built.store) ?? null
}

/**
 * Every built panel's saved state, for the session store's next write.
 *
 * Only panels that opted into save appear; only built panels are asked.
 * A panel never opened this session has nothing new to say.
 */
export function collectPanelStates(): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [id, built] of cache) {
    const state = built.def.save?.(built.store)
    if (state !== undefined) out[id] = state
  }
  return out
}

/** Ids whose chunk has been asked for once - the frame loop calls every frame. */
const prefetched = new Set<string>()

/**
 * Fetch a tool's chunk without opening it, and say nothing if it fails.
 *
 * Called while the student is merely LOOKING at a tool, so the chunk is
 * usually already there by the time they press E. A tool they walk past and
 * never open costs one cached request; a failure here is not an error, because
 * nobody has asked for anything yet - the real open will fail loudly instead.
 *
 * The chunk ONLY - not the panel. This runs mid-walk, and building a panel
 * means running a constructor that stands up (and on some tools solves) a
 * simulation on the main thread. Doing that because a gaze crossed a tool is
 * what made walking the aisle hitch; the constructor now waits for the open,
 * where the camera glide covers it.
 */
export function preloadToolPanel(id: string): void {
  if (prefetched.has(id)) return
  prefetched.add(id)
  void entryFor(id)?.chunk().catch(() => {
    // A failed fetch (a phone that dipped offline) may succeed later - let
    // the next gaze try again rather than remembering the bad luck.
    prefetched.delete(id)
  })
}

/**
 * The panel for this tool, building and seeding it on first ask.
 *
 * Asynchronous only because the panel's code may still be on the wire; once a
 * tool has been opened, every later ask resolves on the spot from `cache`.
 */
export function loadToolPanel(
  id: string,
  services: PanelServices,
  tool: Tool,
): Promise<BuiltPanel> {
  const built = cache.get(id)
  if (built) return Promise.resolve(built)

  const inFlight = pending.get(id)
  if (inFlight) return inFlight

  const found = entryFor(id)
  if (!found) return Promise.reject(new Error(`No panel for live tool '${id}'`))

  const promise = found
    .resolve()
    .then((def): BuiltPanel => {
      // Seed with whatever a previous session saved. Exactly once, at the
      // only moment it is cheap: the store exists but nothing has mounted, so
      // there is no UI to reconcile.
      const stored = storedPanelState(id)
      const result: BuiltPanel = { def, store: def.create(services, tool, stored) }
      cache.set(id, result)
      return result
    })
    .finally(() => pending.delete(id))

  pending.set(id, promise)
  return promise
}
